import { requiredEnumWithFallback } from "@clipboard-health/contract-core";
import { z } from "zod";

// JSON:API type literals from the BE contract
// (`packages/contract-backend-main/src/lib/prnPool.contract.ts`,
// `packages/contract-backend-main/src/lib/prnShift.contract.ts`).
// API_TYPES uses kebab-case for the wire values; the JS property names are
// camelCase but the literal strings in URLs and bodies are dashed.
export const PRN_POOL_WORKER_API_TYPE = "prn-pool-worker";
export const PRN_SHIFT_API_TYPE = "prn-shift";
export const PRN_WORKPLACE_API_TYPE = "workplace";
export const PRN_WORKER_API_TYPE = "worker";

// NURSE is a legacy umbrella qualification that the BE rejects on PRN POST/PATCH.
// We also filter it out of the dropdown client-side.
export const NURSE_QUALIFICATION_NAME = "NURSE";

// JSON:API DTOs. These mirror the merged BE contract verbatim — the
// `contract-backend-main` package version we depend on doesn't export PRN
// schemas yet, so we redeclare locally and swap to the package import once it
// publishes.
const prnPoolWorkerAttributesSchema = z.object({
  name: z.string(),
  email: z.string().nullish(),
  phone: z.string(),
  qualifications: z.array(z.string()).min(1),
  // Optional on the wire so consumers that predate ALCH-6249 don't break;
  // server always emits a value (0 by default).
  baselineWeeklyHours: z.number().nonnegative().optional(),
  // True when the worker exists only on the PRN Roster (User.source ===
  // "PRN_ROSTER"); false when they're linked to a real Clipboard worker
  // account whose name/phone/email is managed on the marketplace side.
  // Drives the Edit dialog's field-disabling: linked workers'
  // profile-shaped fields are read-only because the BE rejects edits
  // with 422 CANNOT_EDIT_NON_PRN_WORKER. Optional on the wire so the FE
  // doesn't break against a BE that predates ALCH-5978 BE PR 2.
  isPrnOnly: z.boolean().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const workerRelationshipSchema = z.object({
  data: z.object({
    type: z.literal(PRN_WORKER_API_TYPE),
    id: z.string(),
  }),
});

const workplaceRelationshipSchema = z.object({
  data: z.object({
    type: z.literal(PRN_WORKPLACE_API_TYPE),
    id: z.string(),
  }),
});

const prnPoolWorkerDtoSchema = z.object({
  type: z.literal(PRN_POOL_WORKER_API_TYPE),
  id: z.string(),
  attributes: prnPoolWorkerAttributesSchema,
  relationships: z.object({
    worker: workerRelationshipSchema,
    workplace: workplaceRelationshipSchema,
  }),
});

export type PrnPoolWorkerDto = z.infer<typeof prnPoolWorkerDtoSchema>;

// Wire-shape responses.
export const listPrnWorkersResponseSchema = z.object({
  data: z.array(prnPoolWorkerDtoSchema),
  links: z
    .object({
      next: z.string().optional(),
      prev: z.string().optional(),
      nextCursor: z.string().optional(),
      prevCursor: z.string().optional(),
    })
    .optional(),
});

export const createPrnWorkerResponseSchema = z.object({
  data: prnPoolWorkerDtoSchema,
  meta: z.object({
    isNewWorker: z.boolean(),
  }),
});

export const updatePrnWorkerResponseSchema = z.object({
  data: prnPoolWorkerDtoSchema,
});

// Bulk create — `POST /api/prn-workers/bulk`. BE always responds 200 with a per-row
// result (partial success); non-200 is only a whole-request failure.
export const BULK_CREATE_MIN_WORKERS = 1;
export const BULK_CREATE_MAX_WORKERS = 200;

export const BULK_WORKER_RESULTS = ["created", "skipped_duplicate", "invalid"] as const;
export type BulkWorkerResult = (typeof BULK_WORKER_RESULTS)[number];

// Email is omitted (not sent as "") when blank — the BE schema rejects an empty string.
export interface BulkWorkerInput {
  name: string;
  phone: string;
  qualifications: string[];
  email?: string;
}

const bulkWorkerInputSchema = z.object({
  name: z.string(),
  phone: z.string(),
  qualifications: z.array(z.string()),
  email: z.string().optional(),
});

export const bulkCreatePrnWorkersRequestSchema = z.object({
  data: z.object({
    type: z.literal(PRN_POOL_WORKER_API_TYPE),
    attributes: z.object({
      workers: z
        .array(bulkWorkerInputSchema)
        .min(BULK_CREATE_MIN_WORKERS)
        .max(BULK_CREATE_MAX_WORKERS),
    }),
    relationships: z.object({
      workplace: workplaceRelationshipSchema,
    }),
  }),
});

const bulkCreatePrnWorkerResultSchema = z.object({
  index: z.number(),
  // Fallback (not bare `z.enum`) so an unknown future BE result doesn't fail the whole parse.
  result: requiredEnumWithFallback(BULK_WORKER_RESULTS),
  data: prnPoolWorkerDtoSchema.optional(),
  reason: z.string().optional(),
});

export const bulkCreatePrnWorkersResponseSchema = z.object({
  data: z.array(bulkCreatePrnWorkerResultSchema),
  meta: z.object({
    total: z.number(),
    created: z.number(),
    skippedDuplicate: z.number(),
    invalid: z.number(),
  }),
});

export type BulkCreatePrnWorkersResponse = z.infer<typeof bulkCreatePrnWorkersResponseSchema>;
export type BulkCreatePrnWorkerResult = z.infer<typeof bulkCreatePrnWorkerResultSchema>;

// Flattened match shape consumed by the Add Roster dialog (and emitted by
// `useLookupPrnWorkerByPhone`, which collapses the BE's JSON:API list
// response — `GET /api/workers?filter[phone][eq]=...&filter[workplace.id][eq]=...`
// from ALF-169 — into one nullable match). Phone is unique on `AgentProfile`,
// so the list can never return more than one entry.
export interface PrnWorkerPhoneMatch {
  workerId: string;
  name: string;
  phone: string;
  email?: string;
  isPrnOnly: boolean;
}

// Wire-shape request bodies.
export const createPrnWorkerRequestSchema = z.object({
  data: z.object({
    type: z.literal(PRN_POOL_WORKER_API_TYPE),
    attributes: z.object({
      name: z.string().trim().min(1).max(100),
      phone: z.string().trim().min(1),
      email: z.string().trim().email().optional(),
      qualifications: z.array(z.string().min(1)).min(1),
      baselineWeeklyHours: z.number().nonnegative().optional(),
    }),
    relationships: z.object({
      workplace: workplaceRelationshipSchema,
    }),
  }),
});

export const updatePrnWorkerRequestSchema = z.object({
  data: z.object({
    type: z.literal(PRN_POOL_WORKER_API_TYPE),
    id: z.string(),
    attributes: z
      .object({
        name: z.string().trim().min(1).max(100).optional(),
        phone: z.string().trim().min(1).optional(),
        email: z.string().trim().email().optional(),
        qualifications: z.array(z.string().min(1)).min(1).optional(),
        baselineWeeklyHours: z.number().nonnegative().optional(),
      })
      .refine((attributes) => Object.keys(attributes).length > 0, {
        message:
          "At least one of name, phone, email, qualifications, or baselineWeeklyHours must be provided",
      }),
  }),
});

// FE-friendly flat shape used by the rest of the feature folder. Hooks unwrap
// the JSON:API envelope into this so views, the form, the row, and the card
// don't have to think about JSON:API.
export const prnWorkerSchema = z.object({
  id: z.string(),
  workerId: z.string(),
  workplaceId: z.string(),
  name: z.string(),
  phone: z.string(),
  email: z.string().optional(),
  qualifications: z.array(z.string()),
  baselineWeeklyHours: z.number().nonnegative(),
  // Defaults to `false` in `unwrapPrnWorker` when the wire payload omits
  // the flag (legacy BE response). Conservatively defaulting to "linked"
  // (not PRN-only) keeps name/phone/email disabled in the Edit dialog
  // for legacy data, matching how the BE `updateRoster` gate rejects
  // edits for any non-PRN_ROSTER source.
  isPrnOnly: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PrnWorker = z.infer<typeof prnWorkerSchema>;

// Form-input shapes.
export interface CreatePrnWorkerInput {
  name: string;
  phone: string;
  email?: string;
  qualifications: string[];
  baselineWeeklyHours?: number;
}

export interface UpdatePrnWorkerInput {
  name?: string;
  phone?: string;
  email?: string;
  qualifications?: string[];
  baselineWeeklyHours?: number;
}

// JSON:API → flat conversion.
export function unwrapPrnWorker(dto: PrnPoolWorkerDto): PrnWorker {
  return {
    id: dto.id,
    workerId: dto.relationships.worker.data.id,
    workplaceId: dto.relationships.workplace.data.id,
    name: dto.attributes.name,
    phone: dto.attributes.phone,
    email: dto.attributes.email ?? undefined,
    qualifications: dto.attributes.qualifications,
    baselineWeeklyHours: dto.attributes.baselineWeeklyHours ?? 0,
    // BE always emits this (post-ALCH-5978 BE PR 2). Default to `false`
    // for legacy responses that predate the flag — conservatively
    // treating the worker as linked keeps the Edit dialog's
    // name/phone/email fields disabled, matching the BE `updateRoster`
    // gate which rejects edits for anything other than PRN_ROSTER source.
    isPrnOnly: dto.attributes.isPrnOnly ?? false,
    createdAt: dto.attributes.createdAt,
    updatedAt: dto.attributes.updatedAt,
  };
}

// ---------------------------------------------------------------------------
// PRN Shift Posting — milestone 2
// ---------------------------------------------------------------------------
// Schemas mirror `prnShift.contract.ts` (BE PRs #24678 / #24785 / #24792 /
// #24798). Both create and list hooks call real BE after ALCH-6012.

// Mirror BE contract's `PRN_POST_TO_MARKETPLACE_MODES` from
// `packages/contract-backend-main/src/lib/prnShiftPosting.contract.ts`.
// Encodes how a PRN posting reaches the marketplace:
//  - "immediately": create marketplace Shift up-front, in parallel with PRN
//    outreach. Roster workers and Clipboard workers race for the same shift.
//  - "on_expiry":   auto-release to marketplace after priority window closes
//    unclaimed (current Window-mode behavior).
//  - "never":       PRN-only; no marketplace fall-through ever.
export const PRN_POST_TO_MARKETPLACE_MODES = ["immediately", "on_expiry", "never"] as const;
export type PrnPostToMarketplaceMode = (typeof PRN_POST_TO_MARKETPLACE_MODES)[number];

const prnShiftPostingShiftDetailsSchema = z.object({
  start: z.string(),
  end: z.string(),
  agentReq: z.string().trim().min(1),
});

export const createPrnShiftPostingRequestSchema = z.object({
  data: z.object({
    type: z.literal(PRN_SHIFT_API_TYPE),
    attributes: z.object({
      shiftDetails: prnShiftPostingShiftDetailsSchema,
      // Omitting both fields triggers the BE auto-derive path from saved
      // RosterWindowSettings. Partial input (only one supplied) is rejected
      // BE-side.
      priorityEndsHoursBeforeShift: z.number().int().min(0).optional(),
      postToMarketplace: z.enum(PRN_POST_TO_MARKETPLACE_MODES).optional(),
    }),
    relationships: z.object({
      workplace: workplaceRelationshipSchema,
    }),
  }),
});

const prnShiftPostingResponseAttributesSchema = z.object({
  groupId: z.string(),
  qualifyingWorkerCount: z.number().int().nonnegative(),
  createdInviteCount: z.number().int().nonnegative(),
  expiresAt: z.string(),
  postToMarketplace: z.enum(PRN_POST_TO_MARKETPLACE_MODES),
});

const prnShiftPostingDtoSchema = z.object({
  type: z.literal(PRN_SHIFT_API_TYPE),
  id: z.string(),
  attributes: prnShiftPostingResponseAttributesSchema,
});

type PrnShiftPostingDto = z.infer<typeof prnShiftPostingDtoSchema>;

export const createPrnShiftPostingResponseSchema = z.object({
  data: prnShiftPostingDtoSchema,
});

// Wire-shape response for `GET /api/prn-shifts` (BE PRs ALCH-5934 / #24798
// + ALCH-6096 / #24849). One row per `groupId` — BE collapses sibling
// invites server-side. `timeSlot` and `postToMarketplace` are kept
// `.optional()` for defense-in-depth: BE owns the enum-fallback for
// `postToMarketplace`, but if anything ever changes wire-side, the FE
// unwrap below substitutes safe defaults rather than rejecting the parse.
// `timeSlot` arrives as `z.string()` — BE pipes through the workplace's
// configured slot name (`shiftDetails.name`), which may include
// custom values outside `am | pm | noc`. The unwrap function narrows.
const prnShiftListItemSchema = z.object({
  groupId: z.string(),
  start: z.string(),
  end: z.string(),
  qualification: z.string(),
  timeSlot: z.string().optional(),
  postToMarketplace: z.enum(PRN_POST_TO_MARKETPLACE_MODES).optional(),
  // Marketplace `Shift._id` when the group has a backing marketplace shift —
  // always set in `immediately` mode, set in `on_expiry` mode after auto-
  // release fires. `null` for groups with no marketplace shift. Lets the
  // daily-view synth path dedupe against the day-shifts response (BE PR
  // ALCH-6121 / #24878). `.optional()` is defense-in-depth so the parse
  // doesn't reject responses from a BE that predates the field.
  shiftId: z.string().nullable().optional(),
  // When the PRN priority window expires (and, for `on_expiry` mode, the
  // shift is opened to the Clipboard marketplace). `null` for
  // `immediately` mode where the shift is open from posting time. BE PR
  // ALCH-6127 / #24926. `.optional()` is defense-in-depth so the parse
  // doesn't reject responses from a BE that predates the field.
  expiresAt: z.string().nullable().optional(),
  inviteCount: z.object({
    total: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
  }),
  claimedBy: z
    .object({
      workerId: z.string(),
      name: z.string(),
    })
    .nullable(),
});

export const getPrnShiftsResponseSchema = z.object({
  workplace: z.object({
    id: z.string(),
    name: z.string(),
    address: z.string().optional(),
  }),
  shifts: z.array(prnShiftListItemSchema),
});

// FE-friendly flat shape used by the rest of the feature folder. The
// chip-render seam (`derivePrnStatus`) and the synth path
// (`synthesizeVirtualPrnShiftCard`) consume this shape; the active-postings
// hook adapts the BE GET response into it.
export const prnShiftPostingSchema = z.object({
  groupId: z.string(),
  workplaceId: z.string(),
  shiftDetails: prnShiftPostingShiftDetailsSchema,
  // `.optional()` is defense-in-depth. BE returns one of the three modes
  // (it uses a `requiredEnumWithFallback` helper), so `undefined` is not
  // expected wire-side — but `derivePrnStatus` still has a defensive
  // branch for it.
  postToMarketplace: z.enum(PRN_POST_TO_MARKETPLACE_MODES).optional(),
  // Threaded through from the BE GET response — non-null when there's a
  // marketplace `Shift` backing this group. The daily-view synth path uses
  // this to suppress the duplicate virtual card; the marketplace shift
  // already surfaces via the day-shifts response with the `Roster Open`
  // chip (ALCH-6011).
  shiftId: z.string().nullable().optional(),
  // ISO timestamp when the priority window ends. `null` (or undefined) for
  // `immediately` mode where the shift is already on the marketplace from
  // posting time. Used by the shift-details panel to render
  // "This shift will be opened to Clipboard marketplace workers at HH:MM"
  // on `on_expiry` postings.
  expiresAt: z.string().nullable().optional(),
  // Wire field name kept for BE compatibility. Post-ALF-112 this counts the
  // rostered workers who can actually book the shift (bookability), not the
  // roster size for the qualification.
  qualifyingWorkerCount: z.number(),
  // Narrowed to the three FE-recognized slots. BE returns the workplace-
  // configured slot name as a free-form string via `timeSlot`; the
  // unwrap function narrows known values through and falls back to the
  // hour-based heuristic for anything else (rare custom slot names).
  timeSlot: z.enum(["am", "pm", "noc"]),
  // Derived from BE's `claimedBy`: `null` → "ACTIVE", set → "BOOKED".
  status: z.enum(["ACTIVE", "BOOKED"]),
  bookedByWorkerId: z.string().optional(),
  // Display name of the worker who claimed this PRN posting, sourced from
  // BE's `claimedBy.name` (BE PR ALCH-6146 / #25053). Used by the
  // daily-view synth path so the virtual card renders the worker on a
  // SCHEDULED row instead of "Unfilled — Booked – Roster" — pre-ALCH-6146
  // the marketplace Shift fed the workerName via the day-shifts response;
  // post-ALCH-6146 `never` / `on_expiry` claims don't create a Shift, so
  // the only source for the name is the PRN list response.
  bookedByWorkerName: z.string().optional(),
});
export type PrnShiftPosting = z.infer<typeof prnShiftPostingSchema>;
export type PrnShiftListItem = z.infer<typeof prnShiftListItemSchema>;

// Form-input shape used by the shift creation form. Maps onto the BE request
// at submit time — see `useCreatePrnShiftPosting`.
export interface CreatePrnShiftPostingInput {
  workplaceId: string;
  shiftDetails: {
    start: string;
    end: string;
    agentReq: string;
  };
  priorityEndsHoursBeforeShift?: number;
  postToMarketplace?: PrnPostToMarketplaceMode;
  // Time-slot the user picked when clicking "Post a new ___ shift" in the
  // AM/PM/NOC column. Not on the BE wire; FE-only (see schema comment above).
  timeSlot: PrnShiftPosting["timeSlot"];
}

// Unwrap the JSON:API create response into the flat shape used by FE.
// Status is "ACTIVE" right after creation; timeSlot is the column the user
// posted from (BE derives its own per-workplace timeSlot but the FE
// remembers the user's intent so the virtual card lands in the same
// column).
export function unwrapPrnShiftPostingCreate(
  dto: PrnShiftPostingDto,
  workplaceId: string,
  shiftDetails: PrnShiftPosting["shiftDetails"],
  timeSlot: PrnShiftPosting["timeSlot"]
): PrnShiftPosting {
  return {
    groupId: dto.attributes.groupId,
    workplaceId,
    shiftDetails,
    postToMarketplace: dto.attributes.postToMarketplace,
    qualifyingWorkerCount: dto.attributes.qualifyingWorkerCount,
    timeSlot,
    status: "ACTIVE",
  };
}

function isKnownTimeSlot(value: string | undefined): value is PrnShiftPosting["timeSlot"] {
  return value === "am" || value === "pm" || value === "noc";
}

// Unwrap a single row from the GET /api/prn-shifts response into the
// FE-flat shape. BE returns `timeSlot` as a free-form string (the
// workplace's configured slot name); narrow it to the FE-recognized
// `am | pm | noc` enum, and fall back to the caller-supplied
// hour-based heuristic for any workplace using a custom slot name.
export function unwrapPrnShiftListItem(
  item: PrnShiftListItem,
  workplaceId: string,
  fallbackTimeSlot: PrnShiftPosting["timeSlot"]
): PrnShiftPosting {
  return {
    groupId: item.groupId,
    workplaceId,
    shiftDetails: {
      start: item.start,
      end: item.end,
      agentReq: item.qualification,
    },
    postToMarketplace: item.postToMarketplace,
    shiftId: item.shiftId,
    expiresAt: item.expiresAt,
    qualifyingWorkerCount: item.inviteCount.total,
    timeSlot: isKnownTimeSlot(item.timeSlot) ? item.timeSlot : fallbackTimeSlot,
    status: item.claimedBy ? "BOOKED" : "ACTIVE",
    bookedByWorkerId: item.claimedBy?.workerId,
    bookedByWorkerName: item.claimedBy?.name,
  };
}

// Const map for chip status — encoded as a const map (codebase convention,
// no enums). The internal identifiers keep the legacy "PRN" prefix; the
// chip *labels* are customer-facing and now read "Roster …" (see
// `ShiftStatusChip.PRN_STATUS_LABELS`). ROSTER_OPEN is the
// post-priority-window state where the shift is open to both the
// internal Roster *and* Clipboard. ROSTER_OFFERED_UNCLAIMED is the
// post-shift-start counterpart for IMMEDIATELY / on_expiry mode shifts
// that went unbooked — the NEVER-mode analogue is PRN_ONLY_UNCLAIMED.
export const PRN_STATUS = {
  PRN_PRIORITY: "PRN_PRIORITY",
  PRN_ONLY: "PRN_ONLY",
  BOOKED_PRN: "BOOKED_PRN",
  PRN_ONLY_UNCLAIMED: "PRN_ONLY_UNCLAIMED",
  ROSTER_OPEN: "ROSTER_OPEN",
  ROSTER_OFFERED_UNCLAIMED: "ROSTER_OFFERED_UNCLAIMED",
} as const;

export type PrnStatus = (typeof PRN_STATUS)[keyof typeof PRN_STATUS];
